import { apiClient } from './api'
import type { ApiResponse } from '@/types/common'

export interface User {
  id: string
  email: string
  name: string
  role: 'student' | 'instructor' | 'admin' | 'affiliate' | 'super_admin'
  permissions?: string[]
  avatar?: string
  phone?: string
  gender?: 'L' | 'P'
  birthDate?: string
  emailVerifiedAt?: string | null
  totpEnabled?: boolean
  createdAt?: string
  updatedAt?: string
}

export interface AuthTokens {
  accessToken: string
  refreshToken: string
}

export interface LoginResponse {
  user: User
  tokens?: AuthTokens
  requires_totp?: boolean
  requires_totp_setup?: boolean
  partial_token?: string
}

export interface RegisterResponse {
  user: User
  tokens: AuthTokens
}

export interface LoginRequest {
  email: string
  password: string
}

export interface RegisterRequest {
  email: string
  password: string
  name: string
  phone?: string
}

// --- OTP ---

export interface OTPSendResponse {
  message: string
  email?: string
}

export interface OTPVerifyRequest {
  code: string
  purpose?: 'registration' | 'email_change' | 'password_reset'
}

export interface BankAccountInfo {
  bank_name: string
  account_number: string
  account_holder: string
}

export interface AffiliateRegisterRequest {
  name: string
  email: string
  password: string
  phone?: string
  affiliate_code: string
  bank_account: BankAccountInfo
}

export interface AffiliateRegisterResponse {
  user: User
  message: string
}

export const authService = {
  register: async (data: RegisterRequest): Promise<ApiResponse<RegisterResponse>> => {
    return apiClient.post<RegisterResponse>('/auth/register', data)
  },

  login: async (data: LoginRequest): Promise<ApiResponse<LoginResponse>> => {
    return apiClient.post<LoginResponse>('/auth/login', data)
  },

  logout: async (): Promise<ApiResponse<void>> => {
    return apiClient.post<void>('/auth/logout')
  },

  getCurrentUser: async (): Promise<ApiResponse<User>> => {
    return apiClient.get<User>('/auth/me')
  },

  registerAffiliate: async (data: AffiliateRegisterRequest): Promise<ApiResponse<AffiliateRegisterResponse>> => {
    return apiClient.post<AffiliateRegisterResponse>('/affiliates/register', data)
  },

  sendOtp: async (purpose: string = 'registration'): Promise<ApiResponse<OTPSendResponse>> => {
    return apiClient.post<OTPSendResponse>('/auth/otp/send', { purpose })
  },

  verifyOtp: async (code: string, purpose: string = 'registration'): Promise<ApiResponse<{ message: string }>> => {
    return apiClient.post<{ message: string }>('/auth/otp/verify', { code, purpose })
  },

  resendOtp: async (purpose: string = 'registration'): Promise<ApiResponse<{ message: string }>> => {
    return apiClient.post<{ message: string }>('/auth/otp/resend', { purpose })
  },

  changeEmail: async (newEmail: string): Promise<ApiResponse<{ message: string }>> => {
    return apiClient.patch<{ message: string }>('/users/me/email', { newEmail })
  },
}
